import { memo } from 'react';
import { Grid } from '@mui/material';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { EstimateChip } from './EstimateChip';
import { GlossaryHelp } from '../shared/GlossaryHelp';
import { KpiCard } from '../shared/KpiCard';
import { NOI_LABEL } from '../shared/metric-glossary';
import { metricValue } from '../shared/metric-value';
import { MetricValue } from './MetricValue';
import {
  ACTUAL_ANNUALIZED_HELP,
  ANNUAL_ESTIMATE_HELP,
  formatRatio,
  incompleteCostHint,
  unavailableHint,
  unclassifiedHint,
} from './metrics-copy';

interface AssetReturnCardsProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

export const AssetReturnCards = memo(({ metrics, scaleAmount }: AssetReturnCardsProps) => {
  const unclassified = metrics.quality.unclassifiedLineCount;
  const cashAttention = unclassified > 0 ? unclassifiedHint(unclassified) : undefined;
  const annualEstimate = metrics.annualEstimate;
  const actualAnnualized = metrics.actualAnnualized;
  const annualNoi = actualAnnualized?.cashNoi ?? annualEstimate?.cashNoi ?? metrics.metrics.cashNoi;
  const marketReturn =
    actualAnnualized?.returnOnMarketValue ??
    annualEstimate?.returnOnMarketValue ??
    metrics.metrics.returnOnMarketValue;
  const costReturn =
    actualAnnualized?.yieldOnCost ?? annualEstimate?.yieldOnCost ?? metrics.metrics.yieldOnCost;
  const annualBadge = actualAnnualized ? (
    <EstimateChip label="Ritmo YTD" help={ACTUAL_ANNUALIZED_HELP} />
  ) : annualEstimate ? (
    <EstimateChip label="Previsión ficha" help={ANNUAL_ESTIMATE_HELP} />
  ) : undefined;
  const fichaNoi = metricValue(annualEstimate?.cashNoi);
  // Only when we fall back to the ficha and the ficha itself has no NOI: when
  // there is no ficha estimate at all, `unavailableHint` gives the right copy.
  const annualEstimateUnavailableHint =
    !actualAnnualized && annualEstimate && fichaNoi === null
      ? 'Completa el alquiler esperado y los gastos normalizados en la ficha de la inversión.'
      : undefined;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <KpiCard
          label={actualAnnualized || annualEstimate ? `${NOI_LABEL} anual` : NOI_LABEL}
          help={
            <GlossaryHelp
              term="noi"
              extra="Es «de caja»: cuenta los cobros y pagos con fecha dentro del periodo."
            />
          }
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulado real ${formatCurrency(scaleAmount(metrics.metrics.cashNoi.value ?? 0))}${fichaNoi === null ? '' : ` · previsión ficha ${formatCurrency(scaleAmount(fichaNoi))}`}`
              : annualEstimate
                ? `Acumulado real ${formatCurrency(scaleAmount(metrics.metrics.cashNoi.value ?? 0))}`
                : 'Ingresos del alquiler menos gastos operativos'
          }
        >
          <MetricValue
            metric={annualNoi}
            format={(value) => formatCurrency(scaleAmount(value))}
            color={(annualNoi.value ?? 0) >= 0 ? 'success.main' : 'error.main'}
            attentionHint={cashAttention}
          />
        </KpiCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <KpiCard
          label="Rentabilidad sobre valor"
          help={`${NOI_LABEL} anual sobre el valor de mercado actual. Responde a si el inmueble sigue rindiendo bien para lo que vale hoy.`}
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulada real ${formatRatio(metrics.metrics.returnOnMarketValue)}${annualEstimate ? ` · previsión ficha ${formatRatio(annualEstimate.returnOnMarketValue)}` : ''}`
              : annualEstimate
                ? `Acumulada real ${formatRatio(metrics.metrics.returnOnMarketValue)}`
                : `${NOI_LABEL} sobre el valor actual de mercado`
          }
        >
          <MetricValue
            metric={marketReturn}
            format={() => formatRatio(marketReturn)}
            color="primary.main"
            unavailableHint={
              annualEstimateUnavailableHint ?? unavailableHint('returnOnMarketValue', metrics)
            }
            attentionHint={cashAttention}
          />
        </KpiCard>
      </Grid>

      <Grid item xs={12} md={4}>
        <KpiCard
          label="Rentabilidad sobre coste"
          help="Lo que renta el inmueble sobre lo que pagaste en su día, incluyendo impuestos, notaría, reforma y mobiliario. En el sector se llama «yield on cost»."
          badge={annualBadge}
          caption={
            actualAnnualized
              ? `Acumulada real ${formatRatio(metrics.metrics.yieldOnCost)}${annualEstimate ? ` · previsión ficha ${formatRatio(annualEstimate.yieldOnCost)}` : ''}`
              : annualEstimate
                ? `Acumulada real ${formatRatio(metrics.metrics.yieldOnCost)}`
                : `${NOI_LABEL} sobre ${formatCurrency(metrics.initialCost.amount)} invertidos`
          }
        >
          <MetricValue
            metric={costReturn}
            format={() => formatRatio(costReturn)}
            color="success.main"
            unavailableHint={
              annualEstimateUnavailableHint ?? unavailableHint('yieldOnCost', metrics)
            }
            attentionHint={metrics.initialCost.complete ? cashAttention : incompleteCostHint}
          />
        </KpiCard>
      </Grid>
    </Grid>
  );
});

AssetReturnCards.displayName = 'AssetReturnCards';
